/**
 * WeeklyScheduleEditor — doctor self-defines their working hours.
 * Mon–Sun rows with day-off toggle + start/end TouchTimePicker per day.
 * Surfaces conflict count via the parent's save handler (409 path).
 */
import { useEffect, useState } from 'react';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Pencil, Save, X, AlertTriangle, Clock } from 'lucide-react';
import TouchTimePicker from './TouchTimePicker';
import { doctorDashboardAPI } from '../lib/api';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const WeeklyScheduleEditor = ({ schedule = {}, canEdit = false, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(schedule);
  const [busy, setBusy] = useState(false);
  const [conflicts, setConflicts] = useState(null);

  useEffect(() => { setDraft(schedule || {}); }, [schedule]);

  const setDay = (day, key, val) => {
    setDraft(prev => {
      const cur = prev[day] || { start: '09:00', end: '17:00' };
      if (key === 'off') {
        if (val) {
          const { [day]: _omit, ...rest } = prev;
          return rest;
        }
        return { ...prev, [day]: cur };
      }
      return { ...prev, [day]: { ...cur, [key]: val } };
    });
  };

  const handleSave = async (force = false) => {
    setBusy(true);
    try {
      const res = await doctorDashboardAPI.updateMySchedule(draft, force);
      toast.success(`Schedule saved${res.data.conflict_count ? ` · ${res.data.conflict_count} conflict(s) ignored` : ''}`);
      setEditing(false);
      setConflicts(null);
      onSaved?.(res.data.schedule || draft);
    } catch (e) {
      if (e.response?.status === 409) {
        const detail = e.response.data?.detail || {};
        setConflicts(detail.conflicts || []);
        toast.warning(detail.message || 'Future appointments conflict with this schedule. Review and Force-Save to apply.');
      } else {
        toast.error(e.response?.data?.detail || 'Failed to save schedule');
      }
    }
    setBusy(false);
  };

  const handleCancel = () => {
    setDraft(schedule || {});
    setEditing(false);
    setConflicts(null);
  };

  return (
    <div data-testid="weekly-schedule-editor">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Clock size={14} /> Weekly Schedule</h3>
        {canEdit && !editing && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="schedule-edit-btn">
            <Pencil size={12} className="mr-1" /> Edit
          </Button>
        )}
        {editing && (
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={handleCancel} data-testid="schedule-cancel-btn"><X size={14} /></Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleSave(false)} disabled={busy} data-testid="schedule-save-btn">
              <Save size={12} className="mr-1" /> Save
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-2">
        {DAYS.map(day => {
          const slot = (editing ? draft : schedule)[day];
          return (
            <div key={day} className={`flex items-center gap-2 p-2 rounded-lg text-sm ${slot ? 'bg-emerald-50' : 'bg-slate-50'}`} data-testid={`schedule-row-${day}`}>
              <span className="w-20 font-medium text-slate-700 capitalize text-xs">{day.substring(0, 3)}</span>
              {editing ? (
                <>
                  <Switch checked={!!slot} onCheckedChange={(v) => setDay(day, 'off', !v)} data-testid={`schedule-toggle-${day}`} />
                  {slot ? (
                    <div className="flex items-center gap-1.5">
                      <TouchTimePicker value={slot.start || '09:00'} onChange={(v) => setDay(day, 'start', v)} className="w-28" testId={`schedule-start-${day}`} />
                      <span className="text-xs text-slate-400">—</span>
                      <TouchTimePicker value={slot.end || '17:00'} onChange={(v) => setDay(day, 'end', v)} className="w-28" testId={`schedule-end-${day}`} />
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400 italic">Off-day</span>
                  )}
                </>
              ) : (
                slot ? (
                  <span className="text-emerald-700 font-medium tabular-nums">
                    {(typeof slot === 'string') ? slot : `${slot.start || '—'} – ${slot.end || '—'}`}
                  </span>
                ) : (
                  <span className="text-red-400 text-xs">Closed</span>
                )
              )}
            </div>
          );
        })}
      </div>

      {conflicts && conflicts.length > 0 && (
        <div className="mt-3 border border-amber-200 bg-amber-50 rounded-lg p-3" data-testid="schedule-conflicts-banner">
          <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5"><AlertTriangle size={12} /> {conflicts.length} future appointment(s) conflict with this schedule</p>
          <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
            {conflicts.slice(0, 10).map((c, i) => (
              <div key={i} className="text-[11px] text-amber-700 flex items-center justify-between bg-white/60 rounded px-2 py-1">
                <span>{c.appointment_date} · {c.time_slot || ''} · {c.patient_name || c.patient_id}</span>
                <span className="text-amber-500 italic">{c._reason}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Button size="sm" variant="outline" onClick={() => setConflicts(null)} data-testid="schedule-conflict-cancel">Cancel</Button>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => handleSave(true)} disabled={busy} data-testid="schedule-conflict-force">
              Force-Save (keep conflicts)
            </Button>
          </div>
          <p className="text-[10px] text-amber-700 mt-1.5">Force-save keeps these appointments — please contact each patient to reschedule.</p>
        </div>
      )}
    </div>
  );
};

export default WeeklyScheduleEditor;
